import { useCallback, useRef, useState, type PointerEvent } from 'react';

/** Downward drag, in px, that closes a sheet (Signal 07: "fecham com arraste"). */
export const SHEET_DRAG_CLOSE_THRESHOLD = 96;

/**
 * Drag-to-close for a bottom sheet. Spread `handleProps` on the sheet's handle and apply
 * `offset` as a downward translate; releasing past the threshold calls `onClose`, anything
 * shorter snaps back. Dragging up never moves the sheet.
 */
export function useSheetDragToClose(
  onClose: () => void,
  threshold: number = SHEET_DRAG_CLOSE_THRESHOLD,
) {
  const startY = useRef<number | null>(null);
  const [offset, setOffset] = useState(0);

  const onPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    startY.current = event.clientY;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, []);

  const onPointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
    if (startY.current == null) return;
    setOffset(Math.max(0, event.clientY - startY.current));
  }, []);

  const finish = useCallback(
    (event: PointerEvent<HTMLElement>) => {
      if (startY.current == null) return;
      const distance = Math.max(0, event.clientY - startY.current);
      startY.current = null;
      setOffset(0);
      if (distance >= threshold) onClose();
    },
    [onClose, threshold],
  );

  return {
    offset,
    dragging: offset > 0,
    handleProps: { onPointerDown, onPointerMove, onPointerUp: finish, onPointerCancel: finish },
  };
}
